<template>
  <div>
    <v-dialog v-model="dialog" @keydown.esc="dialog = false" max-width="400px">
      <v-card>
        <v-form ref="form" lazy-validation>
          <v-card-title>
            <span class="headline">{{ $t("add") }} {{ $t("inference") }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="6">
                  <v-select
                    v-model="inferenceData.source_type"
                    :items="sourceTypeItems"
                    item-text="name"
                    item-value="type"
                    :label="
                      $t('select') +
                      ' ' +
                      $t('source').toLowerCase() +
                      $t('type').toLowerCase() +
                      '*'
                    "
                    :rules="rules.required"
                  >
                  </v-select>
                </v-col>

                <v-col cols="6">
                  <v-select
                    v-model="inferenceData.channel_uuid"
                    :items="channelItems"
                    :label="
                      $t('select') + ' ' + $t('channel').toLowerCase() + '*'
                    "
                    :rules="rules.required"
                  >
                  </v-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-select
                    v-model="inferenceData.list_type"
                    :items="['None', ...listTypeItems]"
                    :label="$t('list_type')"
                    :rules="rules.required"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row v-if="inferenceData.source_type == 'camera'">
                <v-col cols="12">
                  <v-select
                    v-model="inferenceData.camera"
                    :items="cameraItems"
                    item-text="camera_name"
                    item-value="camera_id"
                    return-object
                    :label="
                      $t('select') + ' ' + $t('camera').toLowerCase() + '*'
                    "
                    :rules="rules.required"
                  ></v-select>
                </v-col>

                <v-col cols="12">
                  {{ $t("snapshot") }}
                  <v-img
                    v-if="inferenceData.camera != null && isLoaded"
                    :src="snapshot.data"
                    max-width="400"
                    contain
                  ></v-img>
                </v-col>
              </v-row>

              <v-row v-if="inferenceData.source_type == 'file'">
                <v-file-input
                  v-model="inferenceData.camera"
                  :label="$t('video') + ' ' + $t('file').toLowerCase() + '*'"
                  prepend-icon="mdi-file-video"
                  accept=".mp4, .x-m4v, video/*"
                  :rules="rules.required"
                ></v-file-input>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    v-model="inferenceData.camera_alias"
                    :label="$t('camera') + ' ' + $t('alias').toLowerCase()"
                  >
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <!-- <v-col cols="12">
                  <v-slider
                    v-model="inferenceData.fps"
                    :label="$t('fps')"
                    step="1"
                    min="1"
                    max="3"
                    thumb-label
                    :rules="rules.required"
                  >
                    <template v-slot:append>
                      <v-text-field
                        v-model="inferenceData.fps"
                        class="mt-0 pt-0"
                        type="number"
                        step="1"
                        min="1"
                        max="3"
                      >
                      </v-text-field>
                    </template>
                  </v-slider>
                </v-col> -->

                <v-col cols="12">
                  <v-slider
                    v-model="inferenceData.track_limit"
                    :label="$t('track_limit')"
                    step="1"
                    min="1"
                    max="9"
                    track-color="grey"
                    thumb-label
                    :rules="rules.required"
                  >
                    <template v-slot:append>
                      <v-text-field
                        v-model="inferenceData.track_limit"
                        class="mt-0 pt-0"
                        type="number"
                        step="1"
                        min="1"
                        max="9"
                      >
                      </v-text-field>
                    </template>
                  </v-slider>
                </v-col>
              </v-row>
            </v-container>
            <small>*{{ $t("indicates-required-field") }}</small>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialog = false">
              {{ $t("close") }}
            </v-btn>

            <v-btn color="primary" text @click="addInferenceCamera">
              {{ $t("add") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getCameras, getSnapshot } from "@/endpoint/camera";
import { addInferenceCamera } from "@/endpoint/inferenceCamera";
import { getChannels } from "@/endpoint/channel";
import { random } from "@/plugins/util";

export default {
  name: "InferenceAddDialog",
  data() {
    return {
      dialog: false,
      isLoaded: false,
      rules: {
        required: [(v) => !!v || this.$t("rule-required")],
      },
      inferenceData: {
        source_type: "camera",
        channel_uuid: null,
        camera_alias: "",
        list_type: "None",
        camera: null,
        fps: 1,
        track_limit: 5,
      },
      snapshot: null,
      cameraItems: [],
      sourceTypeItems: [],
      channelTable: null,
      channelItems: [],
      listTypeItems: ["whitelist", "blacklist", "VIP"],
    };
  },
  created() {
    this.init(this);
  },
  watch: {
    "inferenceData.camera"() {
      this.snapshot = null;
      this.isLoaded = false;

      (async () => {
        this.snapshot = await getSnapshot(this.inferenceData.camera.camera_id);
        this.isLoaded = true;
      })();
    },
  },
  methods: {
    random: random,
    async init(scope) {
      scope.channelTable = await getChannels();
      scope.channelItems = Object.keys(
        Object.fromEntries(
          Object.entries(scope.channelTable).filter(
            ([, channel]) => channel.inference_camera_uuid == null
          )
        )
      );

      scope.getSourceType();

      scope.cameraItems = await getCameras();
      scope.cameraItems = [...scope.cameraItems].sort((item1, item2) => {
        if (item1.camera_name.toUpperCase() < item2.camera_name.toUpperCase())
          return -1;
        if (item1.camera_name.toUpperCase() > item2.camera_name.toUpperCase())
          return 1;
        return 0;
      });

      return scope;
    },
    getSourceType() {
      let source_types = [
        {
          name: "camera",
          type: "camera",
        },
        // {
        //   name: "file",
        //   type: "file",
        // },
      ];

      this.sourceTypeItems = source_types;
    },
    addInferenceCamera() {
      if (!this.$refs.form.validate()) {
        return;
      }

      let data = new FormData();
      data.append("source_type", this.inferenceData.source_type);
      data.append("channel_uuid", this.inferenceData.channel_uuid);
      data.append("camera_alias", this.inferenceData.camera_alias);
      data.append("list_type", this.inferenceData.list_type);

      if (this.inferenceData.source_type == "camera") {
        data.append("camera", JSON.stringify(this.inferenceData.camera));
      } else if (this.inferenceData.source_type == "file") {
        let filename = this.inferenceData.camera.name;
        data.append("camera", this.inferenceData.camera, filename);
      }

      data.append("fps", this.inferenceData.fps);
      data.append("track_limit", this.inferenceData.track_limit);

      this.$store.dispatch("openOverlay");

      addInferenceCamera(data)
        .then(() => {
          this.$store.dispatch("closeOverlay");
          this.dialog = false;
          this.$router.go();
        })
        .catch(() => {
          this.$store.dispatch("closeOverlay");
        });
    },
  },
};
</script>
